import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
//Components
import { BreadcrumbContext } from '../Layout/Layout';
import TableConfig from './bookingsTableConfig.json';
import Table from '../../atoms/Table/Table';
//API
import { getAllBookingsV3 } from './bookingsAPI';
import { getCustomersDropdownData } from '../dataAPI';
//Utilities
import { getUserData } from '../../utilities/app';
//slices
import { bookingsAdded, bookingSelected, filtersAdded, getFormattedDate } from './bookingsSlice';
import { customersDropdownDataAdded } from '../../appSlices';
import { toast } from 'react-toastify';

function Bookings() {
  const { setCurrentBreadcrumb } = useContext(BreadcrumbContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { header, visibleColumns, exportableColumns } = TableConfig;
  const bookings = useSelector(state => state.booking.bookings);
  const filters = useSelector(state => state.booking.selectedFilters);
  const [originalGridData, setOriginalGridData] = useState(bookings);
  const [gridData, setGridData] = useState(bookings);
  const [loadingData, setLoadingData] = useState(false);
  const customersDropdownData = useSelector(state => state.appData.customers);
  const [customers, setCustomers] = useState(customersDropdownData);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  const [selectedFilters, setSelectedFilters] = useState(() => {
    const { userType, customerID, isCustomerUser } = getUserData();
    return {
      ...filters,
      customeR_ID: isCustomerUser
        ? customerID
        : filters.customeR_ID || (customersDropdownData.length > 0 ? customersDropdownData[0].customeR_ID : ''),
    };
  });
  const { userType, customerID, isCustomerUser } = getUserData();
  
  useEffect(() => {
    if (!userType) {
      navigate('/signin');
    }
  }, [userType]);

  // Ensure customers data is loaded before calling getBookingsData
  useEffect(() => {
    if (!customersDropdownData || !customersDropdownData.length) {
      getCustomersDropdownData(data => {
        dispatch(customersDropdownDataAdded(data));
        setCustomers(data);
  
        if (!selectedFilters.customeR_ID && data.length > 0) {
          setSelectedFilters(prev => ({
            ...prev,
            customeR_ID: isCustomerUser ? customerID : `${data[0].customeR_ID}`
          }));
        }
      });
    } else {
      setCustomers(customersDropdownData);
  
      if (!selectedFilters.customeR_ID && customersDropdownData.length > 0) {
        setSelectedFilters(prev => ({
          ...prev,
          customeR_ID: isCustomerUser ? customerID : `${customersDropdownData[0].customeR_ID}`
        }));
      }
    }
  }, [customersDropdownData, dispatch]);


  // After customers data is set, load bookings data
  useEffect(() => {
    if (customers.length > 0) {
      getBookingsData(); // Call getBookingsData once customers are set
    }
  }, [customers]); // Dependency on customers to ensure it runs after customers are loaded

  const getBookingsData = () => {
    setLoadingData(true);
    const customerId = selectedFilters.customeR_ID;
    getAllBookingsV3(
      getFormattedDate(selectedFilters.startDate),
      getFormattedDate(selectedFilters.endDate),
      'all',
      0,
      100,
      customerId,
      (data) => {
        setLoadingData(false);
        
        if (data?.status && data?.status !== 200) {
          toast.error(data?.message || 'Something went wrong with Booking fetching, please try again later.');
          setGridData([]);
          setOriginalGridData([]);
          return;
        }
        
        dispatch(bookingsAdded(data));
      }
    );
  };

  useEffect(() => {
    setCurrentBreadcrumb([
      {
        "linkText": "Customers",
        "path": "/customers"
      },
      {
        "linkText": "All Bookings",
        "path": "/bookings"
      }
    ]);
  }, [setCurrentBreadcrumb]);

  const getAccessCodesData = (row, dataKey) => {
    const data = row?.access_codes?.reduce((a, b) => {
      a?.push(b[dataKey]);
      return a;
    }, []);
    return data?.toString();
  };

  const parseBookingData = data => {
    return data.map(row => {
      return {
        ...row,
        name: row.guests && row.guests.length && `${row.guests[0]?.firstname} ${row.guests[0]?.lastname}`,
        email: row.guests && row.guests.length && `${row.guests[0]?.contact_details[1]?.content}`,
        phone: row.guests && row.guests.length && `${row.guests[0]?.contact_details[0]?.content}`,
        address: row.guests && row.guests.length && `${row.guests[0]?.street} ${row.guests[0]?.city} ${row.guests[0]?.state} ${row.guests[0]?.country} ${row.guests[0]?.postcode}`,
        email_sent: row?.email_status?.email_sent ? 'Yes' : 'No',
        email_scheduled: row?.email_status?.email_scheduled ? 'Yes' : 'No',
        pass_created: row?.email_status?.pass_claimed ? 'Yes' : 'No',
        access_code: getAccessCodesData(row, 'access_code'),
        license_plate: getAccessCodesData(row, 'car_rego')
      };
    });
  };

  useEffect(() => {
    const data = parseBookingData(bookings);
    setOriginalGridData(data);
    setGridData(data);
    startDateRef.current.valueAsDate = new Date(selectedFilters.startDate);
    endDateRef.current.valueAsDate = new Date(selectedFilters.endDate);
  }, [bookings]);

  const viewEditRow = (opType, row) => {
    localStorage.setItem('selectedBooking', JSON.stringify(row));
    dispatch(bookingSelected(row));
    navigate(`/bookingview`);
  };

  const setTableFilter = (event) => {
    const { name, value, type } = event.target;
    const formattedDateValue = type === 'date' ? value : value;
    setSelectedFilters({
      ...selectedFilters,
      [name]: formattedDateValue
    });
    dispatch(filtersAdded({
      ...selectedFilters,
      [name]: formattedDateValue
    }));
  };

  const generateDataColumn = (key, value, row) => {
    if (key === "actions") {
      return (
        <div className="bookings-grid-row-actions">
          <button onClick={() => viewEditRow('view', row)}>
            <img src="/assets/customers/view.png" />
          </button>
        </div>
      );
    }
    return value;
  };

  const clearSelectedFilter = () => {
    setSelectedFilters({
      startDate: '',
      endDate: ''
    });
    setGridData([...originalGridData]);
  };

  const renderTableFilters = () => (
    <div className="bookings-filter">
      {!isCustomerUser ? (
        <>
          <label>Filter by</label>
          <select name="customeR_ID" id="customer" value={selectedFilters.customeR_ID} onChange={setTableFilter}>
            {
              customers?.map(customer => (
                <option key={customer.customeR_ID} value={customer.customeR_ID}>
                  {customer.customeR_NAME}
                </option>
              ))
            }
          </select>
        </>
      ) : null }

      <label>Booking Start Date</label>
      <input type="date" name="startDate" id="startDate" valueAsDate={selectedFilters.startDate} ref={startDateRef} onChange={setTableFilter}/>

      <label>Booking End Date</label>
      <input type="date" name="endDate" valueAsDate={selectedFilters.endDate} ref={endDateRef} onChange={setTableFilter}/>

      <button className="bookings-filter-clear" onClick={getBookingsData}>Apply Filter</button>
    </div>
  );

  return (
    <div className="bookings">
      <Table
        renderTableFilters={renderTableFilters}
        TableTitle={`All Bookings`}
        ReportTitle={`All Bookings from date ${getFormattedDate(selectedFilters.startDate)} to ${getFormattedDate(selectedFilters.endDate)}`}
        header={header}
        visibleColumns={visibleColumns}
        originalData={originalGridData}
        data={gridData}
        rowId={'booking_id'}
        exportableColumns={exportableColumns}
        generateDataColumn={generateDataColumn}
        loadingData={loadingData}
      />
    </div>
  );
};

export default Bookings;
